import { useEffect, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { Button, Flex, Loader, Text, Title } from '@gjensidige/builders-components';

import type { CustomerOfferResponse } from '../types';
import { api, ApiError } from '../utils/api';
import { formatDate, formatNok } from '../utils/format';
import { ErrorCircleIcon } from '../icons/ErrorCircleIcon';
import { WarningOctagonIcon } from '../icons/WarningOctagonIcon';
import { CompletionIcon } from './CompletionIcon';

type PageState =
  | { status: 'loading' }
  | { status: 'loaded'; offer: CustomerOfferResponse }
  | { status: 'not-found' }
  | { status: 'expired' }
  | { status: 'accepted' }
  | { status: 'declined' }
  | { status: 'already-handled' }
  | { status: 'error'; message: string };

export function CustomerOfferPage() {
  const { token } = useParams({ from: '/customer/$token' });
  const [state, setState] = useState<PageState>({ status: 'loading' });
  const [isActing, setIsActing] = useState(false);

  useEffect(() => {
    api
      .get<CustomerOfferResponse>(`/v1/open/offers/${token}`)
      .then((offer) => setState({ status: 'loaded', offer }))
      .catch((err) => {
        if (err instanceof ApiError) {
          if (err.status === 404) setState({ status: 'not-found' });
          else if (err.status === 410) setState({ status: 'expired' });
          else if (err.status === 409) setState({ status: 'already-handled' });
          else setState({ status: 'error', message: 'Noe gikk galt. Prøv igjen senere.' });
        } else {
          setState({ status: 'error', message: 'En uventet feil oppstod.' });
        }
      });
  }, [token]);

  const handleAccept = async () => {
    setIsActing(true);
    try {
      await api.post(`/v1/open/offers/${token}/accept`);
      setState({ status: 'accepted' });
    } catch (err) {
      if (err instanceof ApiError && err.status === 410) {
        setState({ status: 'expired' });
      } else {
        setState({ status: 'error', message: 'Noe gikk galt ved aksept. Prøv igjen.' });
      }
    } finally {
      setIsActing(false);
    }
  };

  const handleDecline = async () => {
    setIsActing(true);
    try {
      await api.post(`/v1/open/offers/${token}/decline`);
      setState({ status: 'declined' });
    } catch (err) {
      if (err instanceof ApiError && err.status === 410) {
        setState({ status: 'expired' });
      } else {
        setState({ status: 'error', message: 'Noe gikk galt ved avslag. Prøv igjen.' });
      }
    } finally {
      setIsActing(false);
    }
  };

  // ── Terminal / non-loaded states ─────────────────────────────────────────

  if (state.status === 'loading') {
    return (
      <Flex layout={1} justify="center" align="center" style={{ minHeight: '50vh' }}>
        <Loader variant="spinner" aria-label="Laster tilbud" />
      </Flex>
    );
  }

  if (state.status === 'not-found') {
    return (
      <Flex direction="column" align="center" justify="center" gap="xl" className="status-screen">
        <ErrorCircleIcon />
        <Flex direction="column" align="center" gap="sm">
          <Title as="h1" size="2">
            Tilbudet ble ikke funnet
          </Title>
          <Text>Sjekk at du brukte riktig lenke fra e-posten din.</Text>
        </Flex>
      </Flex>
    );
  }

  if (state.status === 'expired') {
    return (
      <Flex direction="column" align="center" justify="center" gap="xl" className="status-screen">
        <WarningOctagonIcon />
        <Flex direction="column" align="center" gap="sm">
          <Title as="h1" size="2">
            Tilbudet har utløpt
          </Title>
          <Text>Ta kontakt med oss for hjelp.</Text>
        </Flex>
      </Flex>
    );
  }

  if (state.status === 'accepted') {
    return (
      <Flex direction="column" align="center" justify="center" gap="xl" className="status-screen">
        <CompletionIcon />
        <Flex direction="column" align="center" gap="md">
          <Title as="h1" size="2" style={{ textAlign: 'center' }}>
            Tilbudet er akseptert
          </Title>
          <Text style={{ textAlign: 'center' }}>
            Takk for tilliten! Når ferdigbefaringen nærmer seg,
            <br />
            tar eksperten kontakt igjen.
          </Text>
          <Text style={{ textAlign: 'center' }}>
            Dette skjer vanligvis dagen før. Da avtaler dere
            <br />
            praktiske detaljer for gjennomføringen.
          </Text>
          <Text>Nå kan du lukke denne siden</Text>
        </Flex>
      </Flex>
    );
  }

  if (state.status === 'declined') {
    return (
      <Flex direction="column" align="center" justify="center" gap="xl" className="status-screen">
        <ErrorCircleIcon />
        <Flex direction="column" align="center" gap="sm">
          <Title as="h1" size="2">
            Tilbud avslått
          </Title>
          <Text>
            Vi har registrert at du ikke ønsker å gå videre. Ta gjerne kontakt om du ombestemmer
            deg.
          </Text>
        </Flex>
      </Flex>
    );
  }

  if (state.status === 'already-handled') {
    return (
      <Flex direction="column" align="center" justify="center" gap="xl" className="status-screen">
        <CompletionIcon />
        <Flex direction="column" align="center" gap="sm">
          <Title as="h1" size="2">
            Allerede besvart
          </Title>
          <Text>Vi har allerede mottatt ditt svar på dette tilbudet.</Text>
        </Flex>
      </Flex>
    );
  }

  if (state.status === 'error') {
    return (
      <Flex direction="column" align="center" justify="center" gap="xl" className="status-screen">
        <ErrorCircleIcon />
        <Flex direction="column" align="center" gap="sm">
          <Title as="h1" size="2">
            Noe gikk galt
          </Title>
          <Text>{state.message}</Text>
        </Flex>
      </Flex>
    );
  }

  // ── Loaded state ─────────────────────────────────────────────────────────

  const { offer } = state;

  const omBoligen: string[] = [];
  if (offer.housingType) omBoligen.push(`Boligtype: ${offer.housingType}`);
  if (offer.propertySizeM2 != null) omBoligen.push(`Størrelse: ${offer.propertySizeM2} m²`);
  if (offer.propertyBathrooms != null) omBoligen.push(`Ant. bad: ${offer.propertyBathrooms}`);
  if (offer.propertyFloors != null) omBoligen.push(`Ant. etasjer: ${offer.propertyFloors}`);
  if (offer.propertyExtras?.length)
    omBoligen.push(`Andre ting: ${offer.propertyExtras.join(', ')}`);

  const befaringNok =
    offer.drivingFeeNok != null && offer.drivingFeeNok > 0
      ? offer.priceNok - offer.drivingFeeNok
      : offer.priceNok;

  return (
    <div className="supplier-page-bg">
      <Flex layout={1} gap="lg" style={{ maxWidth: '480px', margin: '0 auto', width: '100%' }}>
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <Flex layout={1} gap="sm" align="center" style={{ textAlign: 'center' }}>
          <CompletionIcon />
          <Title as="h1" size="3">
            Ditt tilbud fra Befaringseksperten
          </Title>
          <Text>
            {offer.firstName ? `Hei ${offer.firstName}! ` : ''}
            Takk for henvendelsen din. Vi har gleden av å gi deg et tilbud på oppdraget.
          </Text>
        </Flex>

        {/* ── Offer card ─────────────────────────────────────────────────── */}
        <div className="customer-offer-card">
          <div
            style={{
              width: 'fit-content',
              margin: '0 auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--builders-unit-30)',
            }}
          >
            <Title
              as="h2"
              size="4"
              style={{ textAlign: 'center', whiteSpace: 'nowrap', maxWidth: 'calc(100vw - 80px)' }}
            >
              Detaljer om oppdraget
            </Title>

            {/* Tid for befaring */}
            <div>
              <Text weight={700}>Tid for befaring</Text>
              <Text size="small">
                {offer.offeredDate
                  ? `${formatDate(offer.offeredDate)}${offer.offeredTime ? `, klokken ${offer.offeredTime}` : ''}`
                  : 'Ingen dato oppgitt'}
              </Text>
            </div>

            {/* Adresse */}
            <div>
              <Text weight={700}>Adresse</Text>
              <Text size="small">Adresse: {offer.address}</Text>
              <Text size="small">Postkode: {offer.zipCode}</Text>
              <Text size="small">Sted: {offer.city}</Text>
            </div>

            {/* Om boligen — only if any property details exist */}
            {omBoligen.length > 0 && (
              <div>
                <Text weight={700}>Om boligen</Text>
                {omBoligen.map((line) => (
                  <Text key={line} size="small">
                    {line}
                  </Text>
                ))}
              </div>
            )}

            {/* Pris */}
            <div>
              <Text weight={700}>Pris</Text>
              <Text size="small">Befaring: {formatNok(befaringNok)} inkl. mva</Text>
              {offer.drivingFeeNok === 0 ? (
                <Text size="small">Kjøring inkludert: 0 kr i tillegg</Text>
              ) : (
                offer.drivingFeeNok != null && (
                  <Text size="small">Kjøretillegg: {formatNok(offer.drivingFeeNok)} inkl. mva</Text>
                )
              )}
              <Text weight={700} style={{ marginTop: 'var(--builders-unit-10)' }}>
                Totalt: {formatNok(offer.priceNok)}
              </Text>
            </div>

            {/* Kommentar fra eksperten — only if present */}
            {offer.offerComment && (
              <div>
                <Text weight={700}>Kommentar fra eksperten</Text>
                <Text size="small" style={{ fontStyle: 'italic' }}>
                  {offer.offerComment}
                </Text>
              </div>
            )}
          </div>
        </div>

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        <Flex layout={1} gap="none" align="center" style={{ textAlign: 'center' }}>
          <Text weight={700}>Tilbudet er gyldig frem til</Text>
          <Text>{formatDate(offer.expiresAt)}</Text>
        </Flex>

        <Text size="small" style={{ textAlign: 'center' }}>
          Ved å signere godtar du vilkårene i avtalen
        </Text>

        <div style={{ textAlign: 'center' }}>
          <Button
            size="sm"
            onClick={handleAccept}
            disabled={isActing}
            style={{
              paddingLeft: 'var(--builders-unit-40)',
              paddingRight: 'var(--builders-unit-40)',
            }}
          >
            {isActing ? 'Behandler...' : 'Aksepter tilbudet'}
          </Button>
        </div>

        <div style={{ textAlign: 'center' }}>
          <button
            className="decline-link"
            type="button"
            onClick={handleDecline}
            disabled={isActing}
          >
            Avslå tilbudet
          </button>
        </div>
      </Flex>
    </div>
  );
}
